import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";

import { Modal, Input, Textarea, Button } from "../ui";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

// Color picker icon
const ColorIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
    />
  </svg>
);

const schema = yup.object().shape({
  name: yup
    .string()
    .min(2, "Nome muito curto")
    .max(50, "Nome muito longo")
    .required("Nome é obrigatório"),
  color: yup
    .string()
    .min(3, "Cor inválida")
    .max(9, "Cor inválida")
    .required("Cor é obrigatória"),
  greetingMessage: yup.string(),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const [loading, setLoading] = useState(false);
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [color, setColor] = useState("#1273DE");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      color: "#1273DE",
      greetingMessage: "",
    },
  });

  useEffect(() => {
    const fetchQueue = async () => {
      if (!open) return;

      if (!queueId) {
        reset({
          name: "",
          color: "#1273DE",
          greetingMessage: "",
        });
        setColor("#1273DE");
        return;
      }

      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setValue("name", data.name || "");
        setValue("color", data.color || "#1273DE");
        setValue("greetingMessage", data.greetingMessage || "");
        setColor(data.color || "#1273DE");
      } catch (err) {
        toastError(err);
      }
    };

    fetchQueue();
  }, [queueId, open, reset, setValue]);

  const handleClose = () => {
    reset({
      name: "",
      color: "#1273DE",
      greetingMessage: "",
    });
    setColor("#1273DE");
    onClose();
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    setValue("color", newColor);
  };

  const handleSaveQueue = async (data) => {
    setLoading(true);
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, data);
      } else {
        await api.post("/queue", data);
      }
      toast.success("Fila salva com sucesso");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        queueId
          ? i18n.t("queueModal.title.edit")
          : i18n.t("queueModal.title.add")
      }
      size="md"
      footer={
        <>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("queueModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            form="queue-form"
            loading={loading}
            disabled={loading}
          >
            {queueId
              ? i18n.t("queueModal.buttons.okEdit")
              : i18n.t("queueModal.buttons.okAdd")}
          </Button>
        </>
      }
    >
      <form
        id="queue-form"
        onSubmit={handleSubmit(handleSaveQueue)}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={i18n.t("queueModal.form.name")}
            {...register("name")}
            error={errors.name?.message}
            autoFocus
            placeholder="Nome da fila"
          />

          <div>
            <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
              {i18n.t("queueModal.form.color")}
            </label>
            <div className="relative">
              <div
                className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <span
                  className="w-5 h-5 rounded border border-dark-300 dark:border-dark-600"
                  style={{ backgroundColor: color }}
                />
              </div>
              <input
                {...register("color")}
                readOnly
                onClick={() => setColorPickerModalOpen(true)}
                className="w-full pl-10 pr-10 py-2 text-sm border rounded-lg bg-white dark:bg-dark-800 text-dark-900 dark:text-dark-100 border-dark-300 dark:border-dark-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 cursor-pointer"
                placeholder="#1273DE"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setColorPickerModalOpen(true)}
                  className="text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors"
                >
                  <ColorIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            {errors.color?.message && (
              <p className="mt-1 text-xs text-danger-500">{errors.color?.message}</p>
            )}
          </div>
        </div>

        <ColorPicker
          open={colorPickerModalOpen}
          handleClose={() => setColorPickerModalOpen(false)}
          currentColor={color}
          onChange={handleColorChange}
        />

        <Textarea
          label={i18n.t("queueModal.form.greetingMessage")}
          {...register("greetingMessage")}
          error={errors.greetingMessage?.message}
          rows={5}
          resize="vertical"
          placeholder="Mensagem de saudação ao entrar na fila..."
        />
      </form>
    </Modal>
  );
};

export default QueueModal;
